/**
 * Закреплённые предупреждения экрана переписки — короткие строки, которые
 * НЕ уезжают вместе с историей.
 *
 * 🔴 Зачем. Полные плашки (доверие к устройствам, несохраняемая гостевая
 * сессия) живут над лентой, а с 30.08 вся надстройка прокручивается вместе с
 * перепиской — иначе на телефоне шесть плашек по 113px не оставляли места ни
 * ленте, ни полю ввода. Но у прокрутки есть побочное действие: страница на
 * каждое новое сообщение доскроллит область ВНИЗ, и плашка, стоящая наверху,
 * человеку не показывается вовсе. Предупреждение, которого не видно, равно
 * отсутствующему — а речь про смену ключа, то есть про то, ради чего вообще
 * затевалось сквозное шифрование. Нашло ревью Codex 30.08 (High).
 *
 * Поэтому: полные плашки остаются в прокручиваемой области, а СНАРУЖИ висят
 * короткие строки с кнопкой, которая листает к плашкам. Строка стоит ~30px
 * против 678px плашек.
 *
 * 🔴 Компонент общий для ОБЕИХ сторон (кабинет владельца и публичный
 * `/j/chat`) и намеренно чистый: без сети, без роутера, без axios ЛК — иначе
 * он потянул бы в публичную страницу каркас кабинета (та же причина, по
 * которой отдельно живёт `roomNames.js`).
 */
import React from 'react';

const Line = ({ danger, text, onShow, testId }) => (
  <p
    className={`msgTrustPinned${danger ? ' msgTrustPinnedDanger' : ''}`}
    data-testid={testId}
  >
    {/* 🔴 `role="alert"` висит на ТЕКСТЕ, а не на строке целиком: иначе диктор
        читает срочным сообщением заодно и слово «Показать» (ревью Codex 30.08).
        И только для опасного состояния — «первый контакт» рутина, срочностью
        она не является. */}
    <span role={danger ? 'alert' : undefined}>{text}</span>
    <button type="button" onClick={onShow} data-testid={`${testId}-show`}>
      Показать
    </button>
  </p>
);

/**
 * @param {number} changed   у скольких устройств СМЕНИЛСЯ ключ
 * @param {boolean} fragile  сессия не сохраняется (приватный режим)
 * @param {Function} onShow  пролистать к полным плашкам
 */
/**
 * Строка «отправка остановлена» — решение владельца 07.09: при смене ключа
 * устройства отправка стоит, пока человек не сверит код или не подтвердит
 * отправку явно.
 *
 * Живёт у ПОЛЯ ВВОДА, а не в прокрутке: остановлена именно отправка, и
 * объяснение обязано стоять рядом с кнопкой, которая перестала работать, —
 * в прокрутке его спрятала бы автопрокрутка к свежей реплике (тот же класс,
 * что у закреплённой строки доверия выше).
 *
 * Кнопки «Код сверен» здесь нет намеренно: сверка — это разговор голосом с
 * конкретным человеком, она живёт в плашке этого человека. Здесь только то,
 * что относится к отправке.
 *
 * @param {Array<{principalKey, name}>} blocked  кто остановил (из state
 *        контроллера; в 1:1 name может быть null — текст обходится без имени)
 * @param {Function} onSendAnyway  подтвердить отправку на непроверенный ключ
 */
export const SendBlocked = ({ blocked, onSendAnyway }) => {
  if (!blocked?.length) return null;
  const names = blocked.map((b) => b.name).filter(Boolean);
  const text = names.length === 0
    ? 'Отправка остановлена: ключ устройства собеседника СМЕНИЛСЯ.'
    : names.length === 1
      ? `Отправка остановлена: ключ устройства ${names[0]} СМЕНИЛСЯ.`
      : `Отправка остановлена, ключи устройств сменились: ${names.join(', ')}.`;
  return (
    <div className="msgSendBlocked" data-testid="send-blocked">
      {/* role="alert" на тексте, не на блоке: диктору незачем читать кнопку
          срочным сообщением (тот же довод, что у закреплённой строки). */}
      <span role="alert">
        {text}
        {' '}
        Сверьте код безопасности — или отправьте, если уверены.
      </span>
      <button type="button" onClick={onSendAnyway} data-testid="send-anyway">
        Отправить всё равно
      </button>
    </div>
  );
};

const PinnedWarnings = ({ changed = 0, fragile = false, onShow }) => {
  // 🔴 WEB-23: счётчик «первый контакт» отсюда УБРАН вместе с пропом, а не
  // просто перестал передаваться. Мёртвый проп с живой веткой — это способ
  // тихо воскресить снятое поведение одним словом в JSX (ревью 03.09).
  // «Первый контакт» теперь живёт в панели «Защита переписки»: он состояние
  // по умолчанию, и полоса из-за него горела у всех и всегда.
  //
  // 🔴 Урок, ради которого счётчиков было ДВА (ревью Codex 30.08, Medium):
  // пока считался один, смешанное состояние (у одного ключ сменился, у
  // пятерых первый контакт) давало «Ключи устройств сменились (6)» — тревогу,
  // которой на экране нет, и число, которого нет в плашках. Сюда приходит
  // ТОЛЬКО число сменившихся ключей; если когда-нибудь понадобится второе
  // число, оно обязано быть отдельным, а не слитым с этим.
  const trust = changed > 1
    ? `Ключи устройств сменились (${changed}). Сверьте коды безопасности!`
    : changed === 1
      ? 'Ключ устройства собеседника СМЕНИЛСЯ. Сверьте код безопасности!'
      : null;

  if (!trust && !fragile) return null;

  return (
    <>
      {trust && (
        <Line danger={changed > 0} text={trust} onShow={onShow} testId="trust-pinned" />
      )}
      {fragile && (
        <Line
          danger={false}
          text="Чат не сохранится на этом устройстве — вкладку нельзя закрывать."
          onShow={onShow}
          testId="fragile-pinned"
        />
      )}
    </>
  );
};

export default PinnedWarnings;
